/**
 * ChatwootWidget
 *
 * Injects the Chatwoot live-chat SDK into the page and, once it loads,
 * optionally sets the user's identity so the agent can see who they are.
 *
 * Usage (place once, near the root of your authenticated layout):
 *   <ChatwootWidget userEmail="jane@example.com" userName="Jane Smith" />
 *
 * Widget credentials:
 *   baseUrl      : https://support.cloudgeeks.com.au
 *   websiteToken : Gwz1mcvP3vesrLiVCNYsL3rw
 *
 * The script is injected only once even if the component remounts.
 * On unmount the window.$chatwoot bus is cleared so the widget
 * can be cleanly re-initialised if needed.
 */

import { useEffect } from 'react';

interface ChatwootWidgetProps {
  userEmail?: string;
  userName?: string;
  /** Optional custom attributes visible to support agents */
  customAttributes?: Record<string, string | number | boolean>;
}

// Extend window so TypeScript knows about chatwootSDK / $chatwoot
declare global {
  interface Window {
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (identifier: string, info: {
        name?: string;
        email?: string;
        phone_number?: string;
        custom_attributes?: Record<string, string | number | boolean>;
      }) => void;
      setCustomAttributes: (attrs: Record<string, string | number | boolean>) => void;
      reset: () => void;
    };
  }
}

const CHATWOOT_BASE_URL    = 'https://support.cloudgeeks.com.au';
const CHATWOOT_WIDGET_TOKEN = 'Gwz1mcvP3vesrLiVCNYsL3rw';
const SCRIPT_ID = 'chatwoot-sdk';

export default function ChatwootWidget({
  userEmail,
  userName,
  customAttributes,
}: ChatwootWidgetProps) {
  useEffect(() => {
    // Only inject the script once
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id    = SCRIPT_ID;
      script.src   = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.chatwootSDK?.run({
          websiteToken: CHATWOOT_WIDGET_TOKEN,
          baseUrl: CHATWOOT_BASE_URL,
        });
      };

      document.head.appendChild(script);
    }

    // Once Chatwoot is ready, identify the logged-in user
    function identifyUser() {
      if (!window.$chatwoot) return;
      if (userEmail) {
        window.$chatwoot.setUser(userEmail, {
          email: userEmail,
          name:  userName || userEmail,
          custom_attributes: customAttributes,
        });
      }
      if (customAttributes && !userEmail) {
        window.$chatwoot.setCustomAttributes(customAttributes);
      }
    }

    // Chatwoot fires a custom event when ready
    window.addEventListener('chatwoot:ready', identifyUser);
    // Also attempt immediately in case it already loaded
    identifyUser();

    return () => {
      window.removeEventListener('chatwoot:ready', identifyUser);
    };
  }, [userEmail, userName, customAttributes]);

  // No DOM output — the widget injects its own iframe bubble
  return null;
}
